import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Plus, Pencil, Trash2, Wifi, RefreshCw, ExternalLink, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { api, fmtBytes, fmtDate, formatApiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { SPACES } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const emptyForm = {
    name: '', region: 'nyc3', endpoint: 'https://nyc3.digitaloceanspaces.com',
    bucket: '', access_key: '', secret_key: '', cdn_url: '', status: 'active',
};

const REGIONS = [
    'nyc3', 'ams3', 'sgp1', 'sfo2', 'sfo3', 'fra1', 'syd1', 'blr1', 'tor1',
];

export default function SpacesPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const nav = useNavigate();

    const [spaces, setSpaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [busyRow, setBusyRow] = useState(null); // id currently loading

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/spaces');
            setSpaces(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
    const openEdit = (s) => {
        setEditing(s);
        setForm({
            name: s.name, region: s.region, endpoint: s.endpoint,
            bucket: s.bucket, access_key: s.access_key,
            secret_key: '', // will not prefill
            cdn_url: s.cdn_url || '', status: s.status,
        });
        setOpen(true);
    };

    const submit = async () => {
        setSaving(true);
        try {
            if (editing) {
                // Only send secret_key if user provided a new one
                const { secret_key, ...rest } = form;
                const payload = { ...rest };
                if (secret_key) payload.secret_key = secret_key;
                await api.put(`/spaces/${editing.id}`, payload);
                toast.success('Space updated');
            } else {
                await api.post('/spaces', form);
                toast.success('Space added');
            }
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const testInline = async () => {
        setTesting(true);
        try {
            const { data } = await api.post('/spaces/test', {
                region: form.region, endpoint: form.endpoint, bucket: form.bucket,
                access_key: form.access_key, secret_key: form.secret_key,
            });
            data.ok ? toast.success(data.message) : toast.error(data.message);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setTesting(false); }
    };

    const testSaved = async (id) => {
        setBusyRow(id);
        try {
            const { data } = await api.post(`/spaces/${id}/test`);
            data.ok ? toast.success(data.message) : toast.error(data.message);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyRow(null); }
    };
    const refresh = async (id) => {
        setBusyRow(id);
        try {
            await api.post(`/spaces/${id}/refresh`);
            toast.success('Statistics refreshed');
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyRow(null); }
    };
    const del = async (id) => {
        try { await api.delete(`/spaces/${id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid={SPACES.root} className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-4xl font-black tracking-tight mt-1">
                        {isAdmin ? 'Spaces' : 'My Spaces'}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        {isAdmin
                            ? 'Configured DigitalOcean Spaces buckets. Click any to open the read-only explorer.'
                            : 'Spaces assigned to your account. Read-only access.'}
                    </p>
                </div>
                {isAdmin && (
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                onClick={openCreate}
                                data-testid={SPACES.createButton}
                                className="rounded-sm bg-primary hover:bg-primary/90"
                            >
                                <Plus size={16} className="mr-2" /> Add Space
                            </Button>
                        </DialogTrigger>
                        <DialogContent
                            data-testid={SPACES.createDialog}
                            className="bg-popover border-border rounded-sm max-w-xl"
                        >
                            <DialogHeader>
                                <DialogTitle className="font-display">
                                    {editing ? 'Edit Space' : 'Add DigitalOcean Space'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="grid gap-4 py-2">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Name</Label>
                                        <Input
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            data-testid={SPACES.formName}
                                            className="rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Bucket</Label>
                                        <Input
                                            value={form.bucket}
                                            onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                                            data-testid={SPACES.formBucket}
                                            className="rounded-sm font-mono"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Region</Label>
                                        <Select
                                            value={form.region}
                                            onValueChange={(v) => setForm({
                                                ...form, region: v,
                                                endpoint: `https://${v}.digitaloceanspaces.com`,
                                            })}
                                        >
                                            <SelectTrigger data-testid={SPACES.formRegion} className="rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-popover border-border">
                                                {REGIONS.map((r) => (
                                                    <SelectItem key={r} value={r}>{r}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">Status</Label>
                                        <Select
                                            value={form.status}
                                            onValueChange={(v) => setForm({ ...form, status: v })}
                                        >
                                            <SelectTrigger data-testid={SPACES.formStatus} className="rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-popover border-border">
                                                <SelectItem value="active">Active</SelectItem>
                                                <SelectItem value="inactive">Inactive</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm">Endpoint</Label>
                                    <Input
                                        value={form.endpoint}
                                        onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                                        data-testid={SPACES.formEndpoint}
                                        className="rounded-sm font-mono text-xs"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-sm">Access Key</Label>
                                        <Input
                                            value={form.access_key}
                                            onChange={(e) => setForm({ ...form, access_key: e.target.value })}
                                            data-testid={SPACES.formAccessKey}
                                            className="rounded-sm font-mono text-xs"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-sm">
                                            Secret Key {editing && <span className="text-muted-foreground">(leave blank to keep)</span>}
                                        </Label>
                                        <Input
                                            type="password"
                                            value={form.secret_key}
                                            onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
                                            data-testid={SPACES.formSecretKey}
                                            className="rounded-sm text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm">CDN URL <span className="text-muted-foreground">(optional)</span></Label>
                                    <Input
                                        value={form.cdn_url}
                                        onChange={(e) => setForm({ ...form, cdn_url: e.target.value })}
                                        data-testid={SPACES.formCdnUrl}
                                        className="rounded-sm text-sm"
                                    />
                                </div>
                            </div>
                            <DialogFooter className="gap-2">
                                <Button
                                    variant="outline"
                                    onClick={testInline}
                                    disabled={testing || !form.access_key || !form.secret_key || !form.bucket}
                                    data-testid={SPACES.formTest}
                                    className="rounded-sm"
                                >
                                    {testing ? <Loader2 className="animate-spin mr-2" size={14} /> : <Wifi size={14} className="mr-2" />}
                                    Test connection
                                </Button>
                                <Button
                                    onClick={submit}
                                    disabled={saving}
                                    data-testid={SPACES.formSubmit}
                                    className="rounded-sm bg-primary hover:bg-primary/90"
                                >
                                    {saving && <Loader2 className="animate-spin mr-2" size={14} />}
                                    {editing ? 'Save changes' : 'Add Space'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {loading ? (
                <div className="text-center py-16 text-muted-foreground">Loading Spaces…</div>
            ) : spaces.length === 0 ? (
                <div className="rounded-sm border border-dashed border-border p-12 text-center">
                    <div className="font-display text-xl font-bold">No Spaces yet</div>
                    <p className="text-sm text-muted-foreground mt-2 mb-4">
                        {isAdmin
                            ? 'Add your first DigitalOcean Space to start browsing.'
                            : 'You have not been assigned any Spaces yet.'}
                    </p>
                    {isAdmin && (
                        <Button onClick={openCreate} className="rounded-sm">
                            <Plus size={14} className="mr-2" /> Add Space
                        </Button>
                    )}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {spaces.map((s) => {
                        const stats = s.stats || {};
                        return (
                            <div
                                key={s.id}
                                data-testid={SPACES.row(s.id)}
                                className="rounded-sm border border-border bg-card p-5 flex flex-col"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="min-w-0">
                                        <div className="font-display text-lg font-bold tracking-tight truncate">{s.name}</div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            {s.region} · {s.bucket}
                                        </div>
                                    </div>
                                    <Badge
                                        variant="outline"
                                        className={`rounded-sm text-xs ${
                                            s.status === 'active' ? 'border-emerald-500/50 text-emerald-500' : 'text-muted-foreground'
                                        }`}
                                    >
                                        {s.status}
                                    </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground mt-3 truncate">
                                    {s.endpoint}
                                </div>
                                <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                                    <div>
                                        <div className="text-muted-foreground">Size</div>
                                        <div className="font-semibold text-sm mt-0.5">{fmtBytes(stats.total_size || 0)}</div>
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground">Files</div>
                                        <div className="font-semibold text-sm mt-0.5">{(stats.total_files || 0).toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground">Folders</div>
                                        <div className="font-semibold text-sm mt-0.5">{stats.total_folders || 0}</div>
                                    </div>
                                </div>
                                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                                    <span>Last scan: {fmtDate(s.last_scan)}</span>
                                    <span className="flex items-center gap-1">
                                        {s.connected === true ? (
                                            <><CheckCircle2 size={12} className="text-emerald-500" /> connected</>
                                        ) : s.connected === false ? (
                                            <><XCircle size={12} className="text-destructive" /> error</>
                                        ) : (
                                            <>unscanned</>
                                        )}
                                    </span>
                                </div>
                                <div className="mt-5 flex items-center gap-2 flex-wrap">
                                    <Button
                                        size="sm"
                                        onClick={() => nav(`/explorer/${s.id}`)}
                                        data-testid={SPACES.rowOpen(s.id)}
                                        className="rounded-sm bg-primary hover:bg-primary/90"
                                    >
                                        <ExternalLink size={12} className="mr-1.5" /> Open
                                    </Button>
                                    {isAdmin && (
                                        <>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => testSaved(s.id)}
                                                disabled={busyRow === s.id}
                                                data-testid={SPACES.rowTest(s.id)}
                                                className="rounded-sm"
                                            >
                                                {busyRow === s.id ? <Loader2 size={12} className="animate-spin" /> : <Wifi size={12} />}
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => refresh(s.id)}
                                                disabled={busyRow === s.id}
                                                data-testid={SPACES.rowRefresh(s.id)}
                                                className="rounded-sm"
                                            >
                                                <RefreshCw size={12} />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => openEdit(s)}
                                                data-testid={SPACES.rowEdit(s.id)}
                                                className="rounded-sm"
                                            >
                                                <Pencil size={12} />
                                            </Button>
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        data-testid={SPACES.rowDelete(s.id)}
                                                        className="rounded-sm text-destructive"
                                                    >
                                                        <Trash2 size={12} />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent className="rounded-sm border-border">
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Delete Space?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            Removes the Space config and all assignments. Bucket data is not touched.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                                        <AlertDialogAction
                                                            onClick={() => del(s.id)}
                                                            className="rounded-sm bg-destructive text-destructive-foreground"
                                                        >
                                                            Delete
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
